<template>
  <el-col :span="24" id="jes" v-loading.body="changingPage && !navigating" element-loading-text="Fetching page...">
    <ListViewHeader
      title="JOURNAL ENTRIES"
      ref="listViewHeader"
      :meta="meta"
      :selection="selection"
      :currentPage="currentPage"
      :changePage="changePage"
      :handleBatchUpdateSelection="handleBatchUpdateSelection"
      :batchUpdateOptions="batchUpdateOptions"
      :showFilters="showFilters"
      :toggleFilters="toggleFilters"
      :hasCreatePermission="hasCreatePermission"
      :createRouteName="'NewJe'">
    </ListViewHeader>
    <el-row type="flex" justify="center" align="middle" style="padding: 0px 12px;">
      <Filters
        ref="filters"
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="5"
        :autoApply="false"
        clearAction="clearJeState"
        clearMetadataAction="resetJeMeta"
        searchAction="fetchJesWithQuery"
        transactionType="Je"
        :defaultParams ="customFilterOpts"
        :lineFieldSearch="otherNameFieldEnabled ? { field: 'other_name', transactionType: 'je' } : null"
        @filterCleared="() => $refs.filters.applyFilters()"
        @doneFiltering="getAppliedFilters">
        <template v-for="(f, i) in filters" v-slot:[`${f.type}${i}`]="props" :key="i">
          <component :is="f.type" :properties="f.props" :ref="el => collectFilterRef(`${f.type}_je_ref`, el, i)"/>
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <TransactionsConfList
          v-if="jes"
          name="je_transactions_conf_list"
          ref="transactions_conf_list"
          :aggridColumns="aggridColumns"
          :rows="jes"
          :activeTab="activeTab"
          :baseQuery="baseQuery"
          :currentPage="currentPage"
        />
        <el-row v-if="!meta.count && !isFilterLoading" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import store from '@/store/store'
  import { mapGetters } from 'vuex'
  import Filters from '@/components/RetainedFilters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import ListViewHeader from '@/components/DataEntryListViewHeader'
  import { listViewMixins } from '@/Mixins/TransactionListViews'
  import { filterRefsMixin } from '@/Mixins/FilterRefs'
  import TransactionsConfList from '@/components/SigmaClientNew/TransactionsConfList.vue'
  import { retainFiltersOnEntry } from '@/Mixins/SigmaClientNew/transactionGridFilters'

  export default {
    components: {
      Filters,
      KeywordFilter,
      ListViewHeader,
      TransactionsConfList
    },
    mixins: [ listViewMixins, filterRefsMixin ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        statusChangeModalVisible: false,
        transactionToUpdate: null,
        aggridColumns: ['rowSelect', 'ledgerColumn', 'numberColumn', 'sourceCodeColumn', 'descriptionColumn', 'currencyColumn', 'debitsAmountColumn', 'batchColumn', 'actionsColumn'],
        selectedStatuses: ['DRAFT', 'OPEN', 'AUDITED', 'POSTED', 'CLOSED', 'VOIDED', 'DELETED'],
        customFilterOpts: { exclude_payroll: true }
      }
    },
    methods: {
      async getAppliedFilters () {
        this.$store.dispatch('clearSetJeFilterInfo')
        let extendedFilters = {}
        if (this.$refs.filters.getFilters.search) extendedFilters = { search: this.$refs.filters.getFilters.search }
        sessionStorage.setItem('paginationQuery', JSON.stringify({page: this.currentPage, ...this.$refs.filters.getFilters}))
        sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ page: this.currentPage, extendedFilters: extendedFilters }))
        this.$store.dispatch('setJeFilterInfo', { page: this.currentPage, ...this.$refs.filters.getFilters })
        this.$refs['transactions_conf_list'].extendedFilters = extendedFilters
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selection])
      },
      async changePage (page) {
        try {
          this.changingPage = true
          const searchText = this.$refs.filters.getFilters.search ? this.$refs.filters.getFilters.search : ''
          await this.loadRows(page, { search: searchText })
          sessionStorage.setItem('paginationQuery', JSON.stringify({page: page, ...this.$refs.filters.getFilters}))
          sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ page: page, extendedFilters: { search: this.$refs.filters.getFilters.search } }))
          this.$refs['transactions_conf_list'].restoreSelection(this.selection)
        } catch (e) {
          console.log(e)
        } finally {
          this.changingPage = false
        }
      },
      async filterRetained () {
        const saved = JSON.parse(sessionStorage.getItem(`gridFilterModel:${this.$route.name}`))
        this.$nextTick(() => {
          if (saved && saved.extendedFilters && this.$refs) {
            if (saved.extendedFilters.search && this.$refs.keywordFilter_je_ref) {
              this.$refs.keywordFilter_je_ref.keyword = saved.extendedFilters.search
            }
          }
        })
      }
    },
    computed: {
      ...mapGetters({
        jes: 'getJes',
        meta: 'getJeMetadata',
        currentPage: 'getCurrentJePage',
        getBatchById: 'getBatchById',
        selection: 'getSelectedTransactions',
        getCurrencyByBatchId: 'getCurrencyByBatchId',
        getcurrentFilters: 'getFilters',
        getJeFilterInfo: 'getJeFilterInfo',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions'
      }),
      /**
       * Determines whether the 'other_name' line field is enabled in admin settings
       * @return {boolean}
       */
      otherNameFieldEnabled () {
        const fieldOrderSetting = JSON.parse(this.$store.getters.getUiSettings['LINE_FIELD_ORDER'])
        if (!(fieldOrderSetting && fieldOrderSetting.je && fieldOrderSetting.je.other_name)) {
          return false
        }
        return !fieldOrderSetting.je.other_name.hidden
      },
      /**
       * Criteria this view owns, which the grid's column filters are applied on top of
       */
      baseQuery () {
        return { status__in: this.selectedStatuses, ...this.customFilterOpts }
      },
      hasCreatePermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_journalentry', 'entry.add_journalentry'])
      },
      hasPermission () {
        return {
          read: this.hasReadPermission
        }
      },
      hasReadPermission () {
        let readPermission = ['entry.view_journalentry']
        return this.checkIfUserHasRequiredPermissions(readPermission)
      },
      /**
       * The DC and reversal journals are asked for from the grid's source code filter, by the codes
       * they are held under, rather than from checkboxes of their own
       */
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {}
          }
        ]
      }
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('jes').offsetHeight
          const headerHeight = document.getElementById('list-view-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    },
    beforeUnmount () {
      this.$store.dispatch('updateTransactionSelection', [])
      this.$store.dispatch('updateFilteredTransactionSelection', [])
    },
    async beforeRouteEnter (to, from, next) {
      // Settled before the grid is built, so it only finds filters to put back on a return trip
      retainFiltersOnEntry(to.name, from.name)
      try {
        const promises = [
          store.dispatch('resetJeMeta'),
          store.dispatch('clearJeState', true),
          store.dispatch('fetchUiSetting', { name: 'LINE_FIELD_ORDER' }),
          store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
        ]
        // The source code filter opens on the codes, so they have to be in hand before the grid
        // seeds its filters and loads the list
        if (!store.getters.getAllSourcecodes.length) {
          promises.push(store.dispatch('fetchSourcecodesWithQuery'))
        }
        await Promise.all(promises)
      } catch (e) {
        console.log(e)
      } finally {
        next(vm => {
          vm.filterRetained()
        })
      }
    },
    async beforeRouteLeave (to, from, next) {
      try {
        await store.dispatch('clearPoState', true)
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    }
  }
</script>

<style scoped>
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .el-button--info.is-plain {
    background: #e1f5fe;
    border: 1px solid #bfcbd9;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
</style>
